/**
 * Application shell and pipeline orchestration.
 *
 * Three loops, deliberately separated by cost:
 *   terrain  — panning/zooming reloads the DEM and re-inits the worker (seconds)
 *   solve    — wind, stability or resolution changes re-run Layer 1+2 (~0.2–1 s)
 *   resample — changing the displayed height band only re-slices (milliseconds)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import { MapView, type TerrainLayerData } from './ui/MapView';
import { Sidebar } from './ui/Sidebar';
import { CrossSectionPanel } from './ui/CrossSectionPanel';
import { MapLegend } from './ui/MapLegend';
import { RASTER_FALLBACK, rasterAvailableFor } from './ui/rasterLegend';
import {
  DisclaimerGate,
  FooterDisclaimer,
  acceptDisclaimer,
  hasAcceptedDisclaimer,
} from './ui/Disclaimer';
import { loadSites } from './ui/SiteSearch';
import {
  buildSolveInput,
  currentWind,
  defaultWatermarkAltitude,
  QUALITY_SETTINGS,
  useStore,
} from './state/store';
import { readHash, writeHash } from './state/urlHash';
import { resolveDEM } from './dem/resolveDEM';
import { lonLatToGrid, type BBox, type MetricDomain } from './dem/projection';
import { getSolver, makeCoalescer } from './worker/client';
import { fetchForecast, preferredModel } from './weather/openMeteo';
import type { CrossSection } from './physics/crossSection';

/** Reload terrain when the view has drifted this fraction of the domain span. */
const RELOAD_DRIFT = 0.22;

export default function App() {
  const s = useStore();
  const set = s.set;

  const mapRef = useRef<MlMap | null>(null);
  const [domain, setDomain] = useState<MetricDomain | null>(null);
  const [terrainLayers, setTerrainLayers] = useState<TerrainLayerData | null>(null);
  const [accepted, setAccepted] = useState(() => hasAcceptedDisclaimer());
  const gridRef = useRef<{
    data: Float32Array;
    width: number;
    height: number;
    cellSize: number;
  } | null>(null);
  const demSeq = useRef(0);
  const solveCoalescer = useRef(makeCoalescer<void>()).current;
  const demAbort = useRef<AbortController | null>(null);
  const forecastAbort = useRef<AbortController | null>(null);

  // --- boot: hash + saved sites -------------------------------------------
  useEffect(() => {
    const h = readHash();
    const patch: Record<string, unknown> = { sites: loadSites() };
    if (h) {
      for (const [k, v] of Object.entries(h)) {
        if (k === 'hour') patch.forecastHour = v;
        else patch[k] = v;
      }
    }
    set(patch as never);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- keep the URL in step ------------------------------------------------
  useEffect(() => {
    const t = setTimeout(() => writeHash(useStore.getState()), 350);
    return () => clearTimeout(t);
  }, [
    s.lat, s.lon, s.zoom, s.directionDeg, s.speedKmh, s.stability,
    s.heightBand, s.mode, s.forecastHour, s.quality,
  ]);

  const onMapReady = useCallback((map: MlMap) => {
    mapRef.current = map;
    void loadTerrain(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- terrain loop --------------------------------------------------------
  const loadTerrain = useCallback(
    async (map: MlMap) => {
      const seq = ++demSeq.current;
      demAbort.current?.abort();
      const ctrl = new AbortController();
      demAbort.current = ctrl;

      const st = useStore.getState();
      const b = map.getBounds();
      const bbox: BBox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      const gridSize = QUALITY_SETTINGS[st.quality].gridSize;

      // The glide field belongs to the grid being replaced, so it stops being
      // true the moment a new one starts loading.
      set({ status: { kind: 'loading-dem' }, hikeFlyReady: false });

      try {
        const result = await resolveDEM({
          bbox,
          gridSize,
          signal: ctrl.signal,
          force: st.demProvider === 'auto' ? undefined : st.demProvider,
          onAttempt: (provider) => {
            if (seq === demSeq.current) set({ status: { kind: 'loading-dem', provider } });
          },
        });
        if (seq !== demSeq.current) return;

        gridRef.current = {
          data: result.grid.data,
          width: result.grid.width,
          height: result.grid.height,
          cellSize: result.grid.cellSize,
        };
        setDomain(result.domain);
        set({
          attribution: result.attribution,
          activeProvider: result.provider,
          loadedSpan: result.domain.size,
          loadedCentre: [
            (result.domain.bbox[0] + result.domain.bbox[2]) / 2,
            (result.domain.bbox[1] + result.domain.bbox[3]) / 2,
          ],
        });

        const layers = await getSolver().init(result.grid);
        if (seq !== demSeq.current) return;
        setTerrainLayers({
          width: layers.width,
          height: layers.height,
          elevation: layers.elevation,
          fallX: layers.fallX,
          fallY: layers.fallY,
          // Fetched separately, and only if something asks to draw it.
          requiredGlide: null,
        });
        set({ terrainRange: [layers.minH, layers.maxH], hikeFlyReady: false });

        // Keep the altitude the user chose when it still means something for
        // the ground now loaded; re-anchor it when it does not, so panning from
        // the coast into the Alps does not leave the line underground.
        const alt = useStore.getState().layers.watermarkAltitude;
        if (!(alt > layers.minH && alt < layers.maxH)) {
          useStore.getState().setLayers({
            watermarkAltitude: defaultWatermarkAltitude(layers.minH, layers.maxH),
          });
        }

        await runSolve();
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        if (seq !== demSeq.current) return;
        set({ status: { kind: 'error', message: (err as Error).message } });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Reload terrain when the map has drifted far enough to matter.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const st = useStore.getState();
    if (!st.loadedCentre || !st.loadedSpan) return;

    const b = map.getBounds();
    const span = Math.max(
      (b.getEast() - b.getWest()) * 111320 * Math.cos((s.lat * Math.PI) / 180),
      (b.getNorth() - b.getSouth()) * 110540
    );
    const dLon = (s.lon - st.loadedCentre[0]) * 111320 * Math.cos((s.lat * Math.PI) / 180);
    const dLat = (s.lat - st.loadedCentre[1]) * 110540;
    const drift = Math.hypot(dLon, dLat) / st.loadedSpan;
    const zoomChanged = Math.abs(Math.log2(span / st.loadedSpan)) > 0.35;

    if (drift < RELOAD_DRIFT && !zoomChanged) return;
    // Don't reload mid-flight. An easeTo emits intermediate positions, and
    // acting on one throws away a whole DEM fetch and solve for a view the user
    // is already leaving — very noticeable at the higher resolution tiers. The
    // moveend handler updates lat/lon/zoom, which re-runs this effect.
    if (map.isMoving() || map.isZooming() || map.isRotating()) return;

    const t = setTimeout(() => void loadTerrain(map), 550);
    return () => clearTimeout(t);
  }, [s.lat, s.lon, s.zoom, loadTerrain]);

  // Settings that change the grid itself need a full terrain reload.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = setTimeout(() => void loadTerrain(map), 120);
    return () => clearTimeout(t);
  }, [s.quality, s.demProvider, loadTerrain]);

  // The domain is derived from the viewport, so resizing the window (or
  // rotating a phone) leaves it covering the wrong ground until it is rebuilt.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const map = mapRef.current;
        if (map) void loadTerrain(map);
      }, 400);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timer);
    };
  }, [loadTerrain]);

  // --- solve loop ----------------------------------------------------------
  const runSolve = useCallback(async () => {
    const grid = gridRef.current;
    if (!grid) return;
    const st = useStore.getState();

    await solveCoalescer(async () => {
      set({ status: { kind: 'solving' } });
      try {
        const payload = await getSolver().solve(
          buildSolveInput(st, grid),
          st.heightBand,
          st.liftThreshold
        );
        set({
          slice: payload.slice,
          diagnostics: payload.diagnostics,
          contours: payload.contours,
          status: { kind: 'ready' },
        });
      } catch (err) {
        set({ status: { kind: 'error', message: (err as Error).message } });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wind = currentWind(s);
  useEffect(() => {
    if (!gridRef.current) return;
    const t = setTimeout(() => void runSolve(), 180);
    return () => clearTimeout(t);
  }, [wind.speedMs, wind.directionDeg, s.stability, s.empirical, runSolve]);

  // --- resample loop -------------------------------------------------------
  useEffect(() => {
    if (!gridRef.current || s.status.kind === 'loading-dem') return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const payload = await getSolver().resample(s.heightBand, s.liftThreshold);
        if (!cancelled) {
          set({
            slice: payload.slice,
            diagnostics: payload.diagnostics,
            contours: payload.contours,
          });
        }
      } catch {
        // resample before the first solve — the solve loop will cover it
      }
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.heightBand, s.liftThreshold]);

  // --- forecast ------------------------------------------------------------
  useEffect(() => {
    if (s.mode !== 'live') return;
    forecastAbort.current?.abort();
    const ctrl = new AbortController();
    forecastAbort.current = ctrl;

    const model = s.model === 'best_match' ? preferredModel(s.lat, s.lon) : s.model;
    set({ forecastError: null });

    fetchForecast(s.lat, s.lon, model, ctrl.signal)
      .then((forecast) => {
        const now = Date.now();
        let idx = 0;
        let best = Infinity;
        forecast.hours.forEach((h, i) => {
          const d = Math.abs(new Date(h.time).getTime() - now);
          if (d < best) {
            best = d;
            idx = i;
          }
        });
        set({ forecast, forecastHour: useStore.getState().forecastHour || idx });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        set({ forecastError: `Forecast unavailable: ${err.message}`, forecast: null });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.mode, s.model, Math.round(s.lat * 100), Math.round(s.lon * 100)]);

  // Follow the forecast's stability estimate unless the user has taken over.
  useEffect(() => {
    if (s.mode !== 'live' || !s.stabilityAuto || !s.forecast) return;
    const hour = s.forecast.hours[s.forecastHour];
    if (hour && Math.abs(hour.stability.dial - s.stability) > 0.02) {
      set({ stability: hour.stability.dial });
    }
  }, [s.mode, s.stabilityAuto, s.forecast, s.forecastHour, s.stability, set]);

  // --- hike & fly ----------------------------------------------------------
  //
  // A ray cast from every cell, so it is fetched only once the layer is asked
  // for, and then kept — it depends on the terrain alone and survives every
  // solve, height-band change and wind edit until new ground is loaded.
  useEffect(() => {
    if (s.layers.raster !== 'hikefly') return;
    if (!terrainLayers || terrainLayers.requiredGlide) return;
    const seq = demSeq.current;
    let cancelled = false;
    getSolver()
      .hikeFly()
      .then(({ requiredGlide }) => {
        if (cancelled || seq !== demSeq.current) return;
        setTerrainLayers((prev) => (prev ? { ...prev, requiredGlide } : prev));
        set({ hikeFlyReady: true });
      })
      .catch(() => {
        // init() has not run yet; loading terrain will bring us back here.
      });
    return () => {
      cancelled = true;
    };
  }, [s.layers.raster, terrainLayers, set]);

  // The elevation source is only known after the DEM resolves, so a layer that
  // the source cannot support has to be stepped away from once we find out.
  useEffect(() => {
    if (rasterAvailableFor(s.layers.raster, s.activeProvider)) return;
    s.setLayers({ raster: RASTER_FALLBACK });
  }, [s.activeProvider, s.layers.raster, s]);

  // --- cross-section -------------------------------------------------------
  const [section, setSection] = useState<CrossSection | null>(null);
  useEffect(() => {
    const line = s.crossSectionLine;
    if (!line || !domain || !s.slice) {
      setSection(null);
      return;
    }
    const [a, b] = line;
    const [x0, y0] = lonLatToGrid(domain, a[0], a[1]);
    const [x1, y1] = lonLatToGrid(domain, b[0], b[1]);
    let cancelled = false;
    getSolver()
      .crossSection(x0, y0, x1, y1)
      .then((cs) => !cancelled && setSection(cs))
      .catch(() => !cancelled && setSection(null));
    return () => {
      cancelled = true;
    };
  }, [s.crossSectionLine, domain, s.slice]);

  // --- keep the map in step with programmatic location changes -------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    if (Math.abs(c.lat - s.lat) < 1e-6 && Math.abs(c.lng - s.lon) < 1e-6) return;
    map.easeTo({ center: [s.lon, s.lat], zoom: s.zoom, duration: 700 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.lat, s.lon]);

  const attribution = [
    s.attribution ?? '',
    s.mode === 'live' && s.forecast ? s.forecast.attribution : '',
  ];

  return (
    <div className="app">
      {!accepted && (
        <DisclaimerGate
          onAccept={() => {
            acceptDisclaimer();
            setAccepted(true);
          }}
        />
      )}

      <Sidebar />

      <main className="stage">
        <MapView
          domain={domain}
          slice={s.slice}
          contours={s.contours}
          terrainLayers={terrainLayers}
          onMapReady={onMapReady}
        />
        <StatusBanner />
        {section && (
          <CrossSectionPanel
            section={section}
            onClose={() => set({ crossSectionLine: null })}
          />
        )}
        <MapLegend />
        <FooterDisclaimer attribution={attribution} />
      </main>
    </div>
  );
}

function StatusBanner() {
  const status = useStore((s) => s.status);
  const provider = useStore((s) => s.activeProvider);

  if (status.kind === 'ready' || status.kind === 'idle') {
    return provider ? <div className="status-chip subtle">Terrain: {provider}</div> : null;
  }

  if (status.kind === 'error') {
    return (
      <div className="status-chip error" role="alert">
        {status.message}
      </div>
    );
  }

  return (
    <div className="status-chip" role="status">
      <span className="spinner" aria-hidden="true" />
      {status.kind === 'loading-dem'
        ? `Loading terrain${status.provider ? ` · ${status.provider}` : ''}…`
        : 'Solving airflow…'}
    </div>
  );
}
